import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Lottie from "@components/Lottie";

import { CustomModal } from "@components/Modals";
import { Divider } from "@components/Static";
import {
  ButtonGroup,
  Button,
  Slider,
  Input,
  Calendar,
  Switch,
  Dropdown,
} from "@components/Form";
import { AppointmentReportContext } from "@contexts/AppointmentReport";
import { SuccessAnimation } from "@assets/lottie";
import styles from "./AppointmentReportModal.module.scss";
import { appointmentMeetingKindOptions } from "@utils/appointmentMeetingKindData";
import { appointmentMeetingTypeOptions } from "@utils/appointmentMeetingTypeData";
import { UserContext } from "@contexts/User";
import reportGraphqlInstance from "@services/api/report.graphql";
import CalendarIcon from "@assets/icons/Calendar";
import { useOutside } from "@hooks/useOutside";
import { format } from "date-fns";
import * as Locale from "date-fns/locale";
import { AnimatePresence, motion } from "framer-motion";
import { object, string } from "yup";
import { setFieldForm } from "@utils/helpers";
import { AccordionTab } from "primereact/accordion";
import { useFormik } from "formik";
import PlusSignIcon from "@assets/icons/PlusSign";
import { sendNotify } from "@components/GlobalNotify/GlobalNotify";
import { Tooltip } from "primereact/tooltip";
import {
  GLOBAL_JOURNALS,
  REPORT_TOPICS_CATEGORY_TYPE,
  REPORT_TOPIC_TYPES,
} from "@utils/consts";
import { AccordionCustom } from "@components/Accordion";
import { Arrow } from "@assets/icons";
import { appointmentAdditionalSupportOptions } from "@utils/appointmentAdditionalSupportData";
import accountPersonService from "@services/api/accountPerson.graphql";
import AccountGraphqlInstance from "@services/api/account.graphql";
import { useJournalTypes } from "@hooks/useJournalTypes";
import { useNoteJournalTypeUpdate } from "@hooks/noteAndReport";
import JournalFolderSelectionModal from "@components/JournalSelection/JournalFolderSelectionModal";

const AppointmentReportModal = ({ isOpen }) => {
  const { t, i18n } = useTranslation();
  const { createReport, appointmentReportItem, createAppointmentReportItem } =
    useContext(AppointmentReportContext);

  const { profile: currentProfile } = useContext(UserContext);
  const [updateNoteJournalType] = useNoteJournalTypeUpdate();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedMeetingTypeOptions, setSelectedMeetingTypeOptions] = useState(
    [],
  );
  const {
    journalTypes: hookJournalTypes,
    isLoading: journalTypesLoading,
    error: journalTypesError,
  } = useJournalTypes({
    enabled: isOpen && !appointmentReportItem?.journalTypes?.length,
  });

  const journalTypes =
    appointmentReportItem?.journalTypes?.length > 0
      ? appointmentReportItem.journalTypes
      : hookJournalTypes;

  const [selectedMeetingKindOptions, setSelectedMeetingKindOptions] = useState(
    [],
  );
  const [worryLevel, setWorryLevel] = useState("0");
  const [noteComment, setNoteComment] = useState("");
  const [feedbackAllbry, setFeedbackAllbry] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isAdvanceQuick, setIsAdvanceQuick] = useState(false);
  const [isAnonymousReport, setIsAnonymousReport] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [studentName, setStudentName] = useState("");
  const today = useMemo(() => new Date());
  const [date, setDate] = useState(today);
  const [showCalendar, setShowCalendar] = useState(false);
  const [addReportTopicsLoading, setAddReportTopicsLoading] = useState(false);
  const [reportTopicsCategory, setReportTopicsCategory] = useState([]);
  const [isOpenTopicAddModal, setIsOpenTopicAddModal] = useState(false);
  const [selectTopicCategoryId, setSelectTopicCategoryId] = useState(null);
  const [activeIndexTopicCategory, setActiveIndexTopicCategory] =
    useState(null);
  const [isDigitalMeeting, setIsDigitalMeeting] = useState(false);
  const [showMeetingKind, setShowMeetingKind] = useState(false);
  const [showWorryLevelModal, setShowWorryLevelModal] = useState(false);
  const [isQuick, setIsQuick] = useState(false);
  const [isReportInitialPageScreen, setIsReportInitialPageScreen] =
    useState(true);
  const [shouldSyncToProrenata, setShouldSyncToProrenata] = useState(false);
  const [quickReportTopics, setQuickReportTopics] = useState([]);
  const [gender, setGender] = useState(null);
  const [
    selectedAppointmentAdditionalSupportOptions,
    setSelectedAppointmentAdditionalSupportOptions,
  ] = useState([]);
  const calendarRef = useRef(null);
  useOutside(calendarRef, setShowCalendar);
  const isContinuingJournalSelectionRef = useRef(false);
  const [isProrenataIntegrationActive, setIsProrenataIntegrationActive] =
    useState(false);
  const journals = journalTypes;
  const [isJournalFolderModalOpen, setIsJournalFolderModalOpen] =
    useState(false);

  const [selectedJournalFolder, setSelectedJournalFolder] = useState(null);
  const [hasJournalBeenSelected, setHasJournalBeenSelected] = useState(false);

  const getAllReportTopicsCategory = () => {
    reportGraphqlInstance
      .getAllReportTopicsCategory(REPORT_TOPICS_CATEGORY_TYPE.COUNSELLOR)
      .then((res) => {
        if (res) {
          setReportTopicsCategory(res);
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    const fetchReportTopics = async () => {
      if (isQuick) {
        const topics = await reportGraphqlInstance.getReportTopicsByType(
          REPORT_TOPIC_TYPES.QUICK,
        );
        setQuickReportTopics(topics);
      } else {
        setQuickReportTopics([]);
      }
    };

    fetchReportTopics();
  }, [isQuick]);

  useEffect(() => {
    getAllReportTopicsCategory();
  }, []);

  useEffect(() => {
    if (isOpen && !hasJournalBeenSelected && journals.length > 0) {
      // If journalTypeId already exists, skip modal and mark as selected
      if (appointmentReportItem?.journalTypeId) {
        setHasJournalBeenSelected(true);
        return;
      }

      if (journals.length > 1) {
        setIsJournalFolderModalOpen(true);
      } else if (journals.length === 1) {
        const singleFolder = journals[0];
        setSelectedJournalFolder(singleFolder);
        createAppointmentReportItem({
          ...appointmentReportItem,
          journalTypeId: singleFolder?.id,
        });
        if (appointmentReportItem?.metaNote && singleFolder?.id) {
          updateNoteJournalType(appointmentReportItem.metaNote, singleFolder.id);
        }
        setHasJournalBeenSelected(true);
      }
    }
  }, [
    isOpen,
    hasJournalBeenSelected,
    journals,
    appointmentReportItem,
    createAppointmentReportItem,
  ]);

  useEffect(() => {
    if (appointmentReportItem) {
      appointmentReportItem.type == "anonymous_quick_report"
        ? setIsAnonymousReport(true)
        : setIsAnonymousReport(false);
    }
  }, [appointmentReportItem]);

  useEffect(() => {
    const fetchProrenataStatus = async () => {
      if (!currentProfile?.accountId || !currentProfile?.userId) {
        setIsProrenataIntegrationActive(false);
        setShouldSyncToProrenata(false);
        return;
      }

      try {
        const details = await AccountGraphqlInstance.getLeyrUserDetails(
          currentProfile.accountId,
          currentProfile.userId,
        );
        const isActive = Boolean(details?.leyrUser?.active);
        setIsProrenataIntegrationActive(isActive);
        if (!isActive) {
          setShouldSyncToProrenata(false);
        }
      } catch (error) {
        console.error("Failed to fetch Prorenata status:", error);
        setIsProrenataIntegrationActive(false);
        setShouldSyncToProrenata(false);
      }
    };

    if (isOpen) {
      fetchProrenataStatus();
    } else {
      setIsProrenataIntegrationActive(false);
      setShouldSyncToProrenata(false);
    }
  }, [isOpen, currentProfile?.accountId, currentProfile?.userId]);

  useEffect(() => {
    if (appointmentReportItem) {
      if (
        appointmentReportItem.type == "chat" ||
        appointmentReportItem.type == "appointment"
      ) {
        setIsDigitalMeeting(true);
        setSelectedMeetingKindOptions([
          appointmentMeetingKindOptions.find(
            (x) => x.value == "Digital meeting",
          ),
        ]);
      } else {
        setIsDigitalMeeting(false);
        setSelectedMeetingKindOptions([]);
      }
    }
  }, [appointmentReportItem, isReportInitialPageScreen]);

  useEffect(() => {
    (async () => {
      if (isAnonymousReport && currentProfile) {
        const actualAttr =
          await accountPersonService.getAttributesWithStudentByAccountId(
            currentProfile.accountId,
          );
        const actualAttrModified = [];

        actualAttr.forEach((item) => {
          const existingAttribute = actualAttrModified.find(
            (attr) => attr[item.attributeName] !== undefined,
          );
          if (existingAttribute) {
            existingAttribute[item.attributeName].push(item.attributeValue);
          } else {
            actualAttrModified.push({
              [item.attributeName]: [item.attributeValue],
            });
          }
        });

        const attributeKeys = [
          ...new Set(actualAttrModified.flatMap(Object.keys)),
        ];
        const results = attributeKeys.map((key) => ({
          attributeName: key,
          attributeValue: actualAttrModified
            .filter((obj) => key in obj)
            .flatMap((obj) => obj[key]),
        }));
        setAttributes(results);
      }
    })();
  }, [isAnonymousReport]);

  const onSelectedAttributesChange = (option, x) => {
    const selectedItem = JSON.parse(JSON.stringify(selectedAttributes));
    if (!selectedItem[x.attributeName.value]) {
      selectedItem[x.attributeName.value] = [];
    }
    let found = false;
    const newOptions = selectedItem[x.attributeName.value]
      .map((selectedOption) => {
        if (selectedOption.value === option.value) {
          found = true;
          return null;
        }
        return selectedOption;
      })
      .filter((selectedOption) => !!selectedOption);

    if (!found) {
      newOptions.push(option);
    }
    selectedItem[x.attributeName.value] = newOptions;
    setSelectedAttributes(selectedItem);
  };

  const onChangeWorryLevel = (event) => {
    setWorryLevel(event.target.value);
  };

  const onSelectedOptionsChange = (option) => {
    let found = false;
    const newOptions = selectedOptions
      .map((selectedOption) => {
        if (selectedOption.value === option.value) {
          found = true;
          return null;
        }

        return selectedOption;
      })
      .filter((selectedOption) => !!selectedOption);

    if (!found) {
      newOptions.push(option);
    }

    setSelectedOptions(newOptions);
  };

  const onSelectedMeetingTypeOptionsChange = (option) => {
    let found = false;
    const newOptions = selectedMeetingTypeOptions
      .map((selectedMeetingTypeOption) => {
        if (selectedMeetingTypeOption.value === option.value) {
          found = true;
          return null;
        }
        return selectedMeetingTypeOption;
      })
      .filter((selectedMeetingTypeOption) => !!selectedMeetingTypeOption);

    if (!found) {
      newOptions.push(option);
    }
    setSelectedMeetingTypeOptions(newOptions);
  };

  const onSelectedMeetingKindOptionsChange = (option) => {
    let found = false;
    const newOptions = selectedMeetingKindOptions
      .map((selectedMeetingTypeOption) => {
        if (selectedMeetingTypeOption.value === option.value) {
          found = true;
          return null;
        }
      })
      .filter((selectedMeetingTypeOption) => !!selectedMeetingTypeOption);

    if (!found) {
      newOptions.push(option);
    }
    setSelectedMeetingKindOptions(newOptions);
  };
  let selectedMeetingKindOption = "";
  for (let x of selectedMeetingKindOptions) {
    selectedMeetingKindOption = x.value;
  }

  const onSelectedAppointmentAdditionalSupportOptionsChange = (option) => {
    const found = selectedAppointmentAdditionalSupportOptions.some(
      (selectedOption) => selectedOption.value === option.value,
    );

    if (found) {
      setSelectedAppointmentAdditionalSupportOptions((prevOptions) =>
        prevOptions.filter((prevOption) => prevOption.value !== option.value),
      );
    } else {
      setSelectedAppointmentAdditionalSupportOptions((prevOptions) => [
        ...prevOptions,
        option,
      ]);
    }
  };

  const saveAppointmentReport = async () => {
    setIsLoading(true);
    setIsAdvanceQuick(true);
    setIsLoading(false);
  };

  const getAppointmentType = (type) => {
    switch (type) {
      case "chat":
        return isQuick ? "chat_quick" : type;
      case "appointment":
        return isQuick ? "appointment_quick" : type;
      case "anonymous_quick_report":
        return isQuick ? "anonymous_ultra_quick_report" : type;
      case "instant":
        return isQuick ? "quick" : type;
      default:
        return type;
    }
  };

  useEffect(() => {
    if (appointmentReportItem?.createdAt) {
      setDate(new Date(appointmentReportItem.createdAt));
    }
  }, [appointmentReportItem?.createdAt]);

  const saveAllReport = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      let attributesData = {};
      for (const [key, value] of Object.entries(selectedAttributes)) {
        Object.assign(attributesData, {
          [t(key)]: value.map((v) => t(v.value)),
        });
      }

      const topics = selectedOptions.reduce((result, item) => {
        const categoryIndex = result.findIndex(
          (entry) => entry.category === item.categoryValue,
        );
        if (categoryIndex === -1) {
          result.push({
            category: item.categoryValue,
            value: [item.value],
          });
        } else {
          result[categoryIndex].value.push(item.value);
        }
        return result;
      }, []);
      await createReport({
        data: {
          topics,
          worryLevel,
          meetingKind: selectedMeetingKindOption,
          meetingType: selectedMeetingTypeOptions.map((option) => option.value),
          additionSupport: selectedAppointmentAdditionalSupportOptions.map(
            (option) => option.value,
          ),
          note: noteComment,
          feedback: feedbackAllbry,
          ...(appointmentReportItem?.type === "anonymous_quick_report"
            ? { studentName, gender }
            : {}),
          attributes: attributesData,
          journalTypeId:
            appointmentReportItem?.journalTypeId ??
            journalTypes?.find(
              (type) => type.id === GLOBAL_JOURNALS.GENERAL_RECORD_ID,
            )?.id,
          createdAt: new Date(date).getTime(),
          updatedAt: new Date(date).getTime(),
          type: getAppointmentType(appointmentReportItem.type), // NOTICE: is normal quick report for type 'instant' but the quick quick report for type 'quick' and anonymous quick report for type 'anonymous_quick_report' and anonymous ultra quick report for type 'anonymous_ultra_quick_report'
        },
        shouldSyncToProrenata:
          Boolean(isProrenataIntegrationActive) && shouldSyncToProrenata,
        processTaskId: appointmentReportItem?.processTaskId
          ? appointmentReportItem.processTaskId
          : null,
        processConfigId: appointmentReportItem?.processConfigId
          ? appointmentReportItem.processConfigId
          : null,
      });
      setIsAdvanceQuick(false);
      setIsSaving(false);
      setSelectedOptions([]);
      setWorryLevel(0);
      setSelectedMeetingTypeOptions([]);
      setSelectedMeetingKindOptions([]);
      setNoteComment("");
      setFeedbackAllbry("");
      setStudentName("");
      setSelectedAttributes({});
      setIsAnonymousReport(false);
      setIsSaved(true);
      setDate(today);
      setShowMeetingKind(false);
      setActiveIndexTopicCategory(null);
      setIsReportInitialPageScreen(true);
      setIsQuick(false);
      setGender(null);
      setSelectedAppointmentAdditionalSupportOptions([]);
      setShouldSyncToProrenata(false);
      setSelectedJournalFolder(null);
      setHasJournalBeenSelected(false);
      setIsJournalFolderModalOpen(false);
    } catch (err) {
      setIsSaving(false);
      sendNotify(t("reports.saveError"), "error");
    }
  };

  const closeSavedModal = () => {
    createAppointmentReportItem(null);
    setIsSaved(false);
  };

  const closeModal = () => {
    setIsJournalFolderModalOpen(false);
    setSelectedJournalFolder(null);
    setHasJournalBeenSelected(false);

    if (appointmentReportItem?.isManualReport) {
      createAppointmentReportItem(null);
      setIsAdvanceQuick(false);
      setSelectedOptions([]);
      setWorryLevel(0);
      setSelectedMeetingTypeOptions([]);
      setSelectedMeetingKindOptions([]);
      setNoteComment("");
      setFeedbackAllbry("");
      setStudentName("");
      setSelectedAttributes({});
      setIsAnonymousReport(false);
      setDate(today);
      setShowMeetingKind(false);
      setActiveIndexTopicCategory(null);
      setIsReportInitialPageScreen(true);
      setIsQuick(false);
      setGender(null);
      setSelectedAppointmentAdditionalSupportOptions([]);
    } else {
      setIsSaved(false);
    }
  };

  const handleJournalFolderContinue = (selectedFolder) => {
    isContinuingJournalSelectionRef.current = true;
    setIsJournalFolderModalOpen(false);
    setSelectedJournalFolder(selectedFolder);
    createAppointmentReportItem({
      ...appointmentReportItem,
      journalTypeId: selectedFolder?.id,
    });
    if (appointmentReportItem?.metaNote && selectedFolder?.id) {
      updateNoteJournalType(appointmentReportItem.metaNote, selectedFolder.id);
    }
    setHasJournalBeenSelected(true);
    setIsReportInitialPageScreen(true);
  };

  const handleJournalFolderCancel = () => {
    if (isContinuingJournalSelectionRef.current) {
      isContinuingJournalSelectionRef.current = false;
      return;
    }
    closeModal();
  };

  const onContinue = async () => {
    setIsLoading(true);
    setIsAnonymousReport(false);
    setIsQuick(false);
    setIsReportInitialPageScreen(true);
    setIsLoading(false);
  };

  const onChangeDate = (newDate) => {
    setDate(newDate);
  };

  const closeAddReportTopicsModal = () => {
    setIsOpenTopicAddModal(false);
    setSelectTopicCategoryId(null);
  };

  const {
    handleSubmit,
    setFieldValue,
    values,
    errors,
    setErrors,
    initialValues,
  } = useFormik({
    initialValues: {
      name: "",
      description: "",
    },
    validationSchema: object().shape({
      name: string().required("common.requiredField"),
      description: string().required("common.requiredField"),
    }),
    onSubmit: (form) => onSubmit(form),
  });

  const onSubmit = async (data) => {
    setAddReportTopicsLoading(true);
    const value = data.name.trim().replace(/ /g, "_").toLowerCase();
    const payload = {
      ...data,
      name: data.name.trim(),
      categoryId: selectTopicCategoryId,
      accountId: currentProfile.accountId,
      value: value,
    };

    try {
      reportGraphqlInstance.createReportTopics(payload).then((res) => {
        if (res) {
          closeAddReportTopicsModal();
          setAddReportTopicsLoading(false);
          sendNotify(t("reports.addTopic.success"), "success");
          getAllReportTopicsCategory();
        }
      });
    } catch (err) {
      sendNotify(t("reports.addTopic.error"), "error");
      setAddReportTopicsLoading(false);
      closeAddReportTopicsModal();
    }
  };

  const topicActiveTabExpand = () => {
    setActiveIndexTopicCategory([...Array(reportTopicsCategory.length).keys()]);
  };

  const topicActiveTabCollapse = () => {
    setActiveIndexTopicCategory(null);
  };

  const genders = [
    {
      label: t("profile.edit.gender.male"),
      value: "Male",
    },
    {
      label: t("profile.edit.gender.female"),
      value: "Female",
    },
    {
      label: t("profile.edit.gender.other"),
      value: "Other",
    },
    {
      label: t("profile.edit.gender.private"),
      value: "Private",
    },
  ];

  const anonymousReportStep = () => {
    if (!isLoading && isAnonymousReport) {
      return (
        <>
          <div className="flex flex-col">
            <div className="h-24 px-10 flex flex-row items-center">
              <span className="font-bold-poppins text-3xl text-dawn-gray tracking-tight m-0">
                {t("counsellorReport.anonymousReport.title")}
              </span>
            </div>
            <Divider />
            <div
              className={clsx("flex flex-col px-10", styles.reportModalSize)}
            >
              <div className="pt-3">
                <span className="text-dawn-gray text-xlg font-bold">
                  {t("counsellorReport.anonymousReport.selectAttributeTitle")}
                </span>
                {attributes?.length
                  ? attributes.map((x, index) => {
                      const result = selectedAttributes[x.attributeName] || [];
                      return x.attributeValue?.length ? (
                        <React.Fragment key={`attribute-${index}`}>
                          <span
                            className={clsx(
                              "text-dawn-gray text-xlg block p-2",
                              index === 0 ? "mt-0" : "mt-2",
                            )}
                          >
                            {x.attributeName}
                          </span>
                          <ButtonGroup
                            className="flex flex-row flex-wrap gap-2"
                            variant="report"
                            variantSelected="reportSelected"
                            options={x.attributeValue.map((y) => ({
                              label: y,
                              value: y,
                            }))}
                            selectedOptions={result}
                            onChange={(e) => {
                              onSelectedAttributesChange(e, {
                                ...x,
                                attributeName: {
                                  label: x.attributeName,
                                  value: x.attributeName,
                                },
                              });
                            }}
                          />
                        </React.Fragment>
                      ) : null;
                    })
                  : null}
              </div>
              <div className="pt-3">
                <span className="text-dawn-gray text-xlg font-bold">
                  {t("counsellorReport.anonymousReport.gender")}
                </span>
                <div className="flex flex-row">
                  <Dropdown
                    onChange={({ value }) => setGender(value)}
                    scrollID="gender"
                    placeholder={t("profile.gender")}
                    value={gender}
                    options={genders}
                    className={clsx(
                      "h-14 my-3.5 bg-silver-light flex flex-1 rounded cursor-pointer",
                      styles.dropdownDefault,
                    )}
                  />
                </div>
              </div>
              <div className="pt-3 mb-3">
                <span className="text-dawn-gray text-xlg font-bold">
                  {t("counsellorReport.anonymousReport.studentName")}
                </span>
                <div className="mt-3 flex justify-center items-center">
                  <Input
                    className="w-full"
                    value={studentName}
                    onChange={setStudentName}
                  />
                </div>
              </div>
              <div className="absolute left-0 bottom-0 w-full py-5 px-10 z-10 shadow-top flex flex-row justify-end items-center">
                <Button
                  variant="edit"
                  className="rounded-full w-44 py-3 border-transparent border-solid border"
                  onClick={onContinue}
                  isLoading={isSaving}
                >
                  <span className="text-white">{t("common.continue")}</span>
                </Button>
              </div>
            </div>
          </div>
        </>
      );
    }
  };

  const isReportInitialPage = () => {
    return (
      <div className="flex flex-col">
        <div className="h-24 px-10 flex flex-row items-center">
          {appointmentReportItem?.type === "anonymous_quick_report" ? (
            <div
              className={clsx(
                "flex flex-row items-center cursor-pointer hover:opacity-70 transition-opacity duration-30",
              )}
              onClick={() => {
                setIsAnonymousReport(true);
              }}
            >
              <div className="self-stretch pr-3">
                <Arrow />
              </div>
            </div>
          ) : null}
          <span className="font-bold-poppins text-3xl text-dawn-gray tracking-tight m-0">
            {appointmentReportItem?.type === "anonymous_quick_report"
              ? t("counsellorReport.reportTemplate.anonymousReport")
              : t("reports.title")}
          </span>
        </div>
        <Divider />
        <div className={clsx("flex flex-col justify-center px-10 py-6")}>
          <div
            className="py-3"
            onClick={() => {
              (setIsReportInitialPageScreen(false), setIsQuick(true));
            }}
          >
            <div
              className={clsx(
                "flex flex-col max-w-md cursor-pointer bg-pampas rounded p-6",
                styles.reportTypesCard,
              )}
            >
              <p className="mb-2 text-lg font-medium-poppins text-dawn-gray">
                {t("reports.types.quick.title")}
              </p>
              <span className="text-base font-light-poppins d-block  text-dawn-gray">
                {t("reports.types.quick.description")}
              </span>
            </div>
          </div>
          <div
            className="py-3"
            onClick={() => {
              (setIsReportInitialPageScreen(false), setIsQuick(false));
            }}
          >
            <div
              className={clsx(
                "flex flex-col max-w-md cursor-pointer bg-pampas rounded p-6",
                styles.reportTypesCard,
              )}
            >
              <p className="mb-2 text-lg font-medium-poppins text-dawn-gray">
                {t("reports.types.normal.title")}
              </p>
              <span className="text-base font-light-poppins d-block  text-dawn-gray">
                {t("reports.types.normal.description")}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSteps = () => {
    if (!isLoading && isAdvanceQuick)
      return (
        <div className="flex flex-col">
          <div className="h-16 flex flex-row ">
            <div
              className={clsx(
                "flex flex-row items-center cursor-pointer hover:opacity-70 transition-opacity duration-30",
              )}
              onClick={() => {
                setIsAdvanceQuick(false);
              }}
            >
              <div className="self-stretch pr-3">
                <Arrow />
              </div>
            </div>
            <span className="font-bold-poppins text-3xl text-dawn-gray tracking-tight m-0">
              {t("reports.title2")}
            </span>
          </div>
          <Divider />
          <div className={clsx("flex flex-col", styles.reportModalSize)}>
            <div className="pt-3">
              <span className="text-dawn-gray text-lg font-bold">
                {t("reports.selectMeetingType")}
              </span>
              <div className="mt-3">
                <ButtonGroup
                  className="flex flex-row flex-wrap gap-2"
                  variant="report"
                  variantSelected="reportSelected"
                  options={appointmentMeetingTypeOptions}
                  selectedOptions={selectedMeetingTypeOptions}
                  onChange={onSelectedMeetingTypeOptionsChange}
                />
              </div>
            </div>
            {!isDigitalMeeting && (
              <div className="pt-3">
                <div style={{ marginBottom: "-15px" }}>
                  <span className="text-dawn-gray text-lg font-bold align-sub">
                    {t("reports.selectMeetingKind")}{" "}
                    <span className="text-red text-3xl align-top">*</span>
                  </span>
                </div>
                <div className="flex gap-3">
                  <span className="text-dawn-gray text-base font-medium self-center">
                    {t("reports.meetingKind.allbryMeeting")}
                  </span>
                  <Switch
                    isChecked={showMeetingKind}
                    onToggle={(e) => {
                      setShowMeetingKind(e);
                      if (e) {
                        setSelectedMeetingKindOptions([
                          appointmentMeetingKindOptions.find(
                            (x) => x.value == "Digital meeting",
                          ),
                        ]);
                      } else {
                        setSelectedMeetingKindOptions([]);
                      }
                    }}
                  />
                </div>
                {!showMeetingKind && (
                  <div className="mt-3">
                    <span className="text-dawn-gray text-opacity-50 text-base font-small-poppins self-center">
                      {t("reports.meetingKind.allbryMeetingIfNot")}
                    </span>
                    <ButtonGroup
                      className="flex flex-row flex-wrap gap-2"
                      variant="report"
                      variantSelected="reportSelected"
                      options={appointmentMeetingKindOptions.filter(
                        (x) => x.value !== "Digital meeting",
                      )}
                      selectedOptions={selectedMeetingKindOptions}
                      onChange={onSelectedMeetingKindOptionsChange}
                    />
                  </div>
                )}
              </div>
            )}
            {isProrenataIntegrationActive && (
              <div className="pt-6 flex flex-row w-full justify-between items-start">
                <div className="flex flex-col pr-8">
                  <span className="text-dawn-gray text-lg font-bold">
                    {t("reports.prorenataToggle.title")}
                  </span>
                  <span className="text-dawn-gray text-base font-medium self-center">
                    {t("reports.prorenataToggle.description")}
                  </span>
                </div>
                <Switch
                  isChecked={shouldSyncToProrenata}
                  onToggle={setShouldSyncToProrenata}
                />
              </div>
            )}
            <div className="mt-3 mb-3">
              <span className="text-dawn-gray text-xlg font-bold">
                {t("reports.noteComment", {
                  questionNo: isDigitalMeeting ? 5 : 6,
                })}
              </span>
              <div className="mt-3 flex justify-center items-center">
                <Input
                  className="w-full"
                  value={noteComment}
                  onChange={setNoteComment}
                />
              </div>
            </div>
            <div className="mt-3 mb-3">
              <span className="text-dawn-gray text-xlg font-bold">
                {t("reports.feedbackAllbry", {
                  questionNo: isDigitalMeeting ? 6 : 7,
                })}
              </span>
              <div className="mt-3 flex justify-center items-center">
                <Input
                  className="w-full"
                  value={feedbackAllbry}
                  onChange={setFeedbackAllbry}
                />
              </div>
            </div>
            <div className="pt-3">
              <span className="text-dawn-gray text-lg font-bold">
                {t("reports.additionalSupportQuestionTitle", {
                  questionNo: isDigitalMeeting ? 7 : 8,
                })}
              </span>
              <div className="mt-3">
                <ButtonGroup
                  className="flex flex-row flex-wrap gap-2"
                  variant="report"
                  variantSelected="reportSelected"
                  options={appointmentAdditionalSupportOptions}
                  selectedOptions={selectedAppointmentAdditionalSupportOptions}
                  onChange={onSelectedAppointmentAdditionalSupportOptionsChange}
                />
              </div>
            </div>
          </div>
          <div className="absolute left-0 bottom-0 w-full py-5 px-10 z-10 shadow-top flex flex-row justify-end items-center">
            <Button
              variant="edit"
              className="rounded-full w-44 py-3 border-transparent border-solid border"
              isLoading={isSaving}
              onClick={saveAllReport}
              isDisabled={selectedMeetingKindOptions.length == 0 || isSaving}
            >
              <span className="text-white">{t("common.save")}</span>
            </Button>
          </div>
        </div>
      );
    if (isSaved) {
      return (
        <div className="flex flex-col justify-center items-center">
          <div className="w-96 h-56">
            <Lottie
              options={{
                loop: false,
                autoplay: true,
                animationData: SuccessAnimation,
                rendererSettings: {
                  viewBoxSize: "90 50 760 500",
                },
              }}
              ariaRole="none"
            />
          </div>
          <h1 className="font-bold-poppins text-3xl leading-title text-dawn-gray tracking-tight mb-2">
            {t("reports.participationThanks.title")}
          </h1>
          <span className="text-base text-cloudy-gray max-w-xs text-center mb-12">
            {t("reports.participationThanks.details")}
          </span>
          <Button
            variant="edit"
            className="rounded-full py-3 px-24"
            onClick={closeSavedModal}
            isLoading={isSaving}
          >
            <span className="text-white">
              {t("reports.participationThanks.close")}
            </span>
          </Button>
        </div>
      );
    }

    if (!isLoading && isAnonymousReport) return anonymousReportStep();
    if (!isLoading && isReportInitialPageScreen) return isReportInitialPage();

    if (isQuick) {
      return (
        <div className="flex flex-col">
          <div className="h-24 px-10 flex flex-row items-center">
            <div
              className={clsx(
                "flex flex-row items-center cursor-pointer hover:opacity-70 transition-opacity duration-30",
              )}
              onClick={() => {
                setIsReportInitialPageScreen(true);
                setIsQuick(false);
                setSelectedOptions([]);
                setShowMeetingKind(false);
                setSelectedMeetingKindOptions([]);
              }}
            >
              <div className="self-stretch pr-3">
                <Arrow />
              </div>
            </div>
            <span className="font-bold-poppins text-3xl text-dawn-gray tracking-tight m-0">
              {appointmentReportItem?.type === "anonymous_quick_report"
                ? t("counsellorReport.reportTemplate.anonymousReport")
                : t("reports.title")}
            </span>
          </div>
          <Divider />
          <div className={clsx("flex flex-col px-10", styles.reportModalSize)}>
            <div className="py-9">
              <span className="text-dawn-gray text-xl font-bold">
                {t("reports.types.quick.selectTopics")}
              </span>
              <div className="mt-3">
                <ButtonGroup
                  className="flex flex-row flex-wrap gap-2"
                  variant="report"
                  variantSelected="reportSelected"
                  options={quickReportTopics?.map((topic) => {
                    return {
                      label:
                        i18n.language === "sv" ? topic.nameSW : topic.nameENG,
                      value: topic.value,
                      topic,
                    };
                  })}
                  selectedOptions={selectedOptions}
                  onChange={(e) => {
                    onSelectedOptionsChange({
                      ...e.topic,
                      categoryValue: e.topic.category.value,
                    });
                  }}
                />
              </div>
            </div>
            {!isDigitalMeeting && (
              <div className="pt-3">
                <div style={{ marginBottom: "-15px" }}>
                  <span className="text-dawn-gray text-lg font-bold align-sub">
                    {t("reports.types.quick.selectMeetingKind")}{" "}
                    <span className="text-red text-3xl align-top">*</span>
                  </span>
                </div>
                <div className="flex gap-3">
                  <span className="text-dawn-gray text-base font-medium self-center">
                    {t("reports.meetingKind.allbryMeeting")}
                  </span>
                  <Switch
                    isChecked={showMeetingKind}
                    onToggle={(e) => {
                      setShowMeetingKind(e);
                      if (e) {
                        setSelectedMeetingKindOptions([
                          appointmentMeetingKindOptions.find(
                            (x) => x.value == "Digital meeting",
                          ),
                        ]);
                      } else {
                        setSelectedMeetingKindOptions([]);
                      }
                    }}
                  />
                </div>
                {!showMeetingKind && (
                  <div className="mt-3">
                    <span className="text-dawn-gray text-opacity-50 text-base font-small-poppins self-center">
                      {t("reports.meetingKind.allbryMeetingIfNot")}
                    </span>
                    <ButtonGroup
                      className="flex flex-row flex-wrap gap-2"
                      variant="report"
                      variantSelected="reportSelected"
                      options={appointmentMeetingKindOptions.filter(
                        (x) => x.value !== "Digital meeting",
                      )}
                      selectedOptions={selectedMeetingKindOptions}
                      onChange={onSelectedMeetingKindOptionsChange}
                    />
                  </div>
                )}
              </div>
            )}
            {isProrenataIntegrationActive && (
              <div className="pt-6 flex flex-row w-full justify-between items-start">
                <div className="flex flex-col pr-8">
                  <span className="text-dawn-gray text-lg font-bold">
                    {t("reports.prorenataToggle.title")}
                  </span>
                  <span className="text-dawn-gray text-base font-medium self-center">
                    {t("reports.prorenataToggle.description")}
                  </span>
                </div>
                <Switch
                  isChecked={shouldSyncToProrenata}
                  onToggle={setShouldSyncToProrenata}
                />
              </div>
            )}
          </div>
          <div className="absolute left-0 bottom-0 w-full py-3 px-10 z-10 shadow-top flex flex-row justify-end items-center">
            <Button
              variant="edit"
              className="rounded-full w-44 py-3 border-transparent border-solid border"
              onClick={saveAllReport}
              isLoading={isSaving}
              isDisabled={
                isSaving || isQuick
                  ? selectedMeetingKindOptions.length == 0
                  : !selectedOptions?.length
              }
            >
              <span className="text-white">{t("common.save")}</span>
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        <div className="h-24 px-10 flex flex-row items-center">
          <div
            className={clsx(
              "flex flex-row items-center cursor-pointer hover:opacity-70 transition-opacity duration-30",
            )}
            onClick={() => {
              setSelectedOptions([]);
              setWorryLevel(0);
              setSelectedMeetingTypeOptions([]);
              setSelectedMeetingKindOptions([]);
              setNoteComment("");
              setFeedbackAllbry("");
              setDate(today);
              setShowMeetingKind(false);
              setActiveIndexTopicCategory(null);
              setIsReportInitialPageScreen(true);
              setIsQuick(false);
              setSelectedAppointmentAdditionalSupportOptions([]);
            }}
          >
            <div className="self-stretch pr-3">
              <Arrow />
            </div>
          </div>
          <span className="font-bold-poppins text-3xl text-dawn-gray tracking-tight m-0">
            {appointmentReportItem?.type === "anonymous_quick_report"
              ? t("counsellorReport.reportTemplate.anonymousReport")
              : t("reports.title")}
          </span>
        </div>
        <Divider />
        <div className={clsx("flex flex-col px-10", styles.reportModalSize)}>
          <div className="pt-9">
            <span className="text-dawn-gray text-xl font-bold">
              {t("reports.selectDate")}
            </span>
            <div className="mt-2">
              <div className="flex pt-3 flex-row relative">
                <Button
                  onClick={() => setShowCalendar(!showCalendar)}
                  className={clsx(
                    "justify-center items-center bg-silver-light h-14 rounded py-5 px-4 border border-solid border-transparent",
                    showCalendar && "pointer-events-none",
                  )}
                  variant="custom"
                >
                  <span className="text-dawn-gray mr-4">
                    {format(date, "EEEE, MMMM d", {
                      locale: Locale[i18n.language],
                    })}
                  </span>
                  <CalendarIcon width={22} height={20} />
                </Button>
                <AnimatePresence>
                  {showCalendar && (
                    <motion.div
                      ref={calendarRef}
                      className={clsx(
                        "absolute left-60 rounded-md z-10 flex flex-row bg-milk",
                        styles.calendarShadow,
                      )}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, translateY: "0.625rem" }}
                      exit={{ opacity: 0, translateY: "0.625rem" }}
                    >
                      <Calendar
                        withoutShadow
                        maxDate={today}
                        value={date}
                        onChange={onChangeDate}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
          <div className="pt-9">
            <span className="text-dawn-gray text-xl font-bold">
              {t("reports.selectTopics")}
            </span>
            <div className={clsx("mt-9")}>
              <div className="text-right mb-4">
                <button onClick={topicActiveTabExpand}>
                  <span className="text-dawn-gray font-medium-poppins">
                    {t("reports.addTopic.expandTab")}
                  </span>
                </button>{" "}
                &nbsp;&nbsp;/ &nbsp;&nbsp;
                <button onClick={topicActiveTabCollapse}>
                  <span className="text-dawn-gray font-medium-poppins">
                    {t("reports.addTopic.collapseTab")}
                  </span>
                </button>
                <span className="text-dawn-gray font-medium-poppins">
                  &nbsp;&nbsp;{t("reports.addTopic.all")}
                </span>
              </div>
              <AccordionCustom
                multiple
                activeIndex={activeIndexTopicCategory}
                onTabChange={(e) => setActiveIndexTopicCategory(e.index)}
              >
                {reportTopicsCategory.length > 0 &&
                  reportTopicsCategory?.map((category, i) => (
                    <AccordionTab
                      header={
                        i18n.language == "enUS"
                          ? category?.nameENG
                          : category?.nameSW
                      }
                      key={i}
                      className="font-medium-poppins text-dawn-gray"
                    >
                      {category?.reportTopics.map((topic, index) => (
                        <div
                          className={clsx("mx-1 inline-block")}
                          key={index}
                          onClick={() => {
                            onSelectedOptionsChange({
                              ...topic,
                              categoryValue: category.value,
                            });
                          }}
                        >
                          <Tooltip
                            target={`.without${index}`}
                            position={"top"}
                            style={{ maxWidth: "400px" }}
                          />
                          <div
                            className={clsx(
                              "mt-2",
                              styles.OptionsBox,
                              selectedOptions.some(
                                (option) => option.value === topic.value,
                              )
                                ? styles.selectedBox
                                : "",
                            )}
                          >
                            <div className="justify-center py-8 rounded-3xl">
                              <h5>
                                {i18n.language == "enUS"
                                  ? topic.nameENG
                                  : topic.nameSW}
                              </h5>{" "}
                              &nbsp;&nbsp;
                              <i
                                className={`without${index} proceed pi pi-question-circle cursor-pointer`}
                                data-pr-tooltip={
                                  i18n.language == "enUS"
                                    ? topic.descriptionENG
                                    : topic.descriptionSW
                                }
                                data-pr-position="top"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <div
                        className={clsx("mx-1 inline-block")}
                        onClick={() => {
                          setIsOpenTopicAddModal(true);
                          setSelectTopicCategoryId(category.id);
                        }}
                      >
                        <div className={clsx("mt-2", styles.OptionsBox)}>
                          <div className="justify-center py-8 rounded-3xl">
                            <h5 className="flex">
                              {t("reports.addTopic.button")} &nbsp;&nbsp;{" "}
                              <PlusSignIcon />
                            </h5>
                          </div>
                        </div>
                      </div>
                    </AccordionTab>
                  ))}
              </AccordionCustom>
            </div>
          </div>
          <div className="mt-11 mb-16">
            <span className="text-dawn-gray text-xl font-bold">
              {t("reports.concernScale")}
            </span>
            <div className="mt-6 flex justify-center items-center">
              <Slider
                max={5}
                className="w-full"
                value={worryLevel}
                onChange={onChangeWorryLevel}
              />
            </div>
            <div className="flex flex-row justify-center mt-3 items-center">
              <span className="text-dawn-gray text-opacity-50 rounded-md cursor-pointer font-bold text-center">
                {t(
                  `reports.worriedLevel.level${
                    worryLevel && worryLevel?.replace(".", "_")
                  }`,
                )}
                <span
                  onClick={() => {
                    setShowWorryLevelModal(true);
                  }}
                >
                  <i
                    className="without0 proceed pi pi-question-circle text-dawn-gray cursor-pointer ml-2 align-text-bottom"
                    style={{ fontSize: "18px", fontWeight: 300 }}
                  />
                </span>
              </span>
            </div>
          </div>
        </div>
        <div className="absolute left-0 bottom-0 w-full py-3 px-10 z-10 shadow-top flex flex-row justify-end items-center">
          <Button
            variant="edit"
            className="rounded-full px-16 py-3 border-transparent border-solid border"
            onClick={saveAppointmentReport}
            isLoading={isSaving}
            isDisabled={!selectedOptions?.length}
          >
            <span className="text-white">{t("common.next")}</span>
          </Button>
        </div>
        <CustomModal
          openModal={isOpenTopicAddModal}
          setOpenModal={closeAddReportTopicsModal}
          hideCloseButton={false}
          className={clsx(
            "bg-milk rounded-xl flex justify-center h-full	py-8",
            styles.reportModal,
          )}
        >
          <form onSubmit={handleSubmit}>
            <h4 className="text-3xl text-center font-bold-poppins text-dawn-gray mb-14 max-w-2xl">
              {t("reports.addTopic.title")}
            </h4>
            <div className="w-full max-w-sm flex flex-col mb-16">
              <Input
                onChange={(val) =>
                  setFieldForm(setFieldValue, setErrors, "name", val)
                }
                placeholder="reports.addTopic.name"
                className="mb-6"
                value={values.name}
                error={errors.name}
              />
              <Input
                onChange={(val) =>
                  setFieldForm(setFieldValue, setErrors, "description", val)
                }
                placeholder="reports.addTopic.description"
                value={values.description}
                error={errors.description}
              />
            </div>
            <Button
              isLoading={addReportTopicsLoading}
              type="submit"
              className="rounded-full m-auto px-16 py-3"
              isDisabled={
                !!Object.keys(errors).length || initialValues === values
                  ? true
                  : addReportTopicsLoading
              }
            >
              <span className="text-white font-medium-poppins">
                {t("common.add")}
              </span>
            </Button>
          </form>
        </CustomModal>
        <CustomModal
          openModal={showWorryLevelModal}
          setOpenModal={() => setShowWorryLevelModal(false)}
          className="bg-milk rounded-xl pt-4 pb-8 w-130"
        >
          <div className="flex flex-col justify-center items-center">
            <span className="text-dawn-gray text-2xl font-bold mb-4 px-8">
              {t("reports.worriedLevel.title")} - {worryLevel}
            </span>
            <Divider />
            <div className="mt-6 px-8 w-full">
              <span className="text-dawn-gray text-opacity-50 rounded-md cursor-pointer whitespace-pre-line">
                {t(
                  `reports.worriedLevel.details${
                    worryLevel && worryLevel?.replace(".", "_")
                  }`,
                )}
              </span>
            </div>
          </div>
        </CustomModal>
      </div>
    );
  };

  return (
    <>
      {journals.length > 1 && isJournalFolderModalOpen && (
        <JournalFolderSelectionModal
          isOpen={isJournalFolderModalOpen}
          onClose={handleJournalFolderCancel}
          onContinue={handleJournalFolderContinue}
          journals={journals}
        />
      )}
      <CustomModal
        openModal={(isOpen && hasJournalBeenSelected) || isSaved}
        setOpenModal={closeModal}
        hideCloseButton={isSaved || !appointmentReportItem?.isManualReport}
        className={clsx(
          "bg-milk rounded-xl",
          styles.reportModal,
          isAdvanceQuick ? "p-5" : "pb-20",
          isSaved ? "p-5" : "pb-20",
        )}
      >
        {renderSteps()}
      </CustomModal>
    </>
  );
};

export default AppointmentReportModal;
